import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { LayoutDashboard, Building2, TrendingUp, Settings, LogOut, Calendar, Package, CheckCircle2, Euro, Users, Key, MessageSquare, XCircle, Loader2, BarChart3, Clock, Phone, Mail, UserCheck, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import CalendarView from '../components/CalendarView';
import MobileNav from '../components/MobileNav';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import Logo from '../components/Logo';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [todos, setTodos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refundingId, setRefundingId] = useState(null);
  
  // États pour la confirmation d'activité
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmingActivity, setConfirmingActivity] = useState(null);
  const [confirmDate, setConfirmDate] = useState('');
  const [confirmLoading, setConfirmLoading] = useState(false);
  
  // États pour l'annulation d'activité
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelingActivity, setCancelingActivity] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelDetails, setCancelDetails] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) setUser(JSON.parse(userStr));

      const [propertiesRes, todosRes] = await Promise.all([
        api.get('/properties'),
        api.get('/dashboard/todos'),
      ]);

      setProperties(propertiesRes.data);
      setTodos(todosRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleTodo = async (todoId, currentStatus) => {
    try {
      if (currentStatus) {
        await api.put(`/dashboard/todos/${todoId}/uncomplete`);
      } else {
        await api.put(`/dashboard/todos/${todoId}/complete`);
      }
      // Mettre à jour localement pour feedback immédiat
      setTodos(prevTodos => prevTodos.map(todo => 
        todo.id === todoId 
          ? { ...todo, task_completed: !currentStatus }
          : todo
      ).sort((a, b) => {
        // Trier: non-complétées en premier, puis par date
        if (a.task_completed !== b.task_completed) {
          return a.task_completed ? 1 : -1;
        }
        return new Date(a.date_debut) - new Date(b.date_debut);
      }));
      toast.success(currentStatus ? 'Tâche décochée' : 'Tâche terminée !');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleRefund = async (todoId, optionName) => {
    if (!window.confirm(`Êtes-vous sûr de vouloir annuler et rembourser l'option "${optionName}" ?\n\nLe voyageur sera remboursé et recevra un email de notification.`)) {
      return;
    }
    
    setRefundingId(todoId);
    try {
      const response = await api.post(`/dashboard/todos/${todoId}/refund`);
      toast.success(response.data.message || 'Remboursement effectué avec succès');
      // Retirer la tâche de la liste
      setTodos(prevTodos => prevTodos.filter(todo => todo.id !== todoId));
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors du remboursement';
      toast.error(errorMsg);
    } finally {
      setRefundingId(null);
    }
  };
  
  // Ouvrir la modale de confirmation d'activité
  const openConfirmDialog = (todo) => {
    setConfirmingActivity(todo);
    setConfirmDate(todo.date_souhaitee || '');
    setConfirmDialogOpen(true);
  };
  
  // Confirmer l'activité
  const handleConfirmActivity = async () => {
    if (!confirmingActivity) return;
    
    setConfirmLoading(true);
    try {
      const response = await api.post(`/dashboard/activity/${confirmingActivity.id}/confirm`, {
        date_confirmee: confirmDate || null
      });
      toast.success(response.data.message || 'Activité confirmée avec succès !');
      setConfirmDialogOpen(false);
      setConfirmingActivity(null);
      fetchData(); // Rafraîchir la liste
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de la confirmation';
      toast.error(errorMsg);
    } finally {
      setConfirmLoading(false);
    }
  };
  
  // Ouvrir la modale d'annulation d'activité
  const openCancelDialog = (todo) => {
    setCancelingActivity(todo);
    setCancelReason('');
    setCancelDetails('');
    setCancelDialogOpen(true);
  };
  
  // Annuler et rembourser l'activité
  const handleCancelActivity = async () => {
    if (!cancelingActivity || !cancelReason) {
      toast.error('Veuillez sélectionner un motif d\'annulation');
      return;
    }
    
    if (cancelReason === 'other' && !cancelDetails.trim()) {
      toast.error('Veuillez préciser le motif d\'annulation');
      return;
    }
    
    setCancelLoading(true);
    try {
      const response = await api.post(`/dashboard/activity/${cancelingActivity.id}/cancel-refund`, {
        reason: cancelReason,
        reason_details: cancelDetails.trim() || null
      });
      toast.success(response.data.message || 'Activité annulée et remboursée');
      
      if (response.data.alert_prestataire) {
        toast.warning(response.data.alert_prestataire, { duration: 8000 });
      }
      
      setCancelDialogOpen(false);
      setCancelingActivity(null);
      fetchData(); // Rafraîchir la liste
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Erreur lors de l\'annulation';
      toast.error(errorMsg);
    } finally {
      setCancelLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate('/auth');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/50 flex items-center justify-center">
        <div className="text-slate-600">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Desktop Sidebar - Hidden on mobile */}
      <aside className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-white border-r border-slate-100 p-6 space-y-8">
        <div className="flex items-center">
          <Logo variant="full" size="h-12" />
        </div>

        <nav className="space-y-2">
          <Button
            variant="ghost"
            data-testid="nav-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            className="w-full justify-start text-rose-600 bg-rose-50 hover:bg-rose-100"
          >
            <LayoutDashboard className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Dashboard
          </Button>
          
          {/* Navigation Concierge uniquement */}
          {user?.role !== 'admin' && (
            <>
              <Button
                variant="ghost"
                data-testid="nav-properties-btn"
                onClick={() => navigate('/properties')}
                className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <Building2 className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Logements
              </Button>
              <Button
                variant="ghost"
                data-testid="nav-revenue-btn"
                onClick={() => navigate('/revenue')}
                className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <TrendingUp className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Revenus
              </Button>
            </>
          )}
          
          {/* Navigation Admin uniquement */}
          {user?.role === 'admin' && (
            <>
              <Button
                variant="ghost"
                data-testid="nav-admin-btn"
                onClick={() => navigate('/admin/options')}
                className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <Settings className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Options Globales
              </Button>
              <Button
                variant="ghost"
                data-testid="nav-admin-revenue-btn"
                onClick={() => navigate('/admin/revenue')}
                className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <Euro className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Paiements Concierges
              </Button>
              <Button
                variant="ghost"
                data-testid="nav-admin-concierges-btn"
                onClick={() => navigate('/admin/concierges')}
                className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <Users className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Liste Concierges
              </Button>
              <Button
                variant="ghost"
                data-testid="nav-admin-analytics-btn"
                onClick={() => navigate('/admin/analytics')}
                className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
              >
                <BarChart3 className="w-5 h-5 mr-3" strokeWidth={1.5} />
                Analytics
              </Button>
            </>
          )}
        </nav>

        <div className="absolute bottom-6 left-6 right-6 space-y-2">
          <ChangePasswordDialog />
          <Button
            variant="ghost"
            data-testid="nav-logout-btn"
            onClick={handleLogout}
            className="w-full justify-start text-slate-600 hover:bg-slate-100 hover:text-slate-900"
          >
            <LogOut className="w-5 h-5 mr-3" strokeWidth={1.5} />
            Déconnexion
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Header - Mobile optimized */}
        <header className="bg-white border-b border-slate-100 px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            {/* Mobile menu */}
            {user?.role !== 'admin' && (
              <MobileNav user={user} onLogout={handleLogout} />
            )}
            
            {/* Mobile logo */}
            <div className="flex items-center lg:hidden">
              <Logo variant="full" size="h-10" />
            </div>
            
            <div className="hidden lg:block">
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard</h1>
              <p className="text-sm sm:text-base text-slate-600 mt-1">Bienvenue, {user?.nom}</p>
            </div>
            
            {/* Placeholder for layout balance on mobile */}
            <div className="lg:hidden w-10"></div>
          </div>
          
          {/* Mobile title */}
          <div className="lg:hidden mt-4">
            <h1 className="text-xl font-semibold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Dashboard</h1>
            <p className="text-sm text-slate-600 mt-1">Bienvenue, {user?.nom}</p>
          </div>
        </header>

        {/* Dashboard Grid */}
        <main className="p-4 sm:p-6 lg:p-8">
          {user?.role === 'admin' ? (
            /* Dashboard Admin */
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card 
                  className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate('/admin/options')}
                  data-testid="admin-options-card"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-4 bg-rose-50 rounded-xl">
                      <Settings className="w-8 h-8 text-rose-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Options Globales</h3>
                      <p className="text-sm text-slate-500">Gérer les options disponibles pour les concierges</p>
                    </div>
                  </div>
                </Card>
                
                <Card 
                  className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate('/admin/revenue')}
                  data-testid="admin-revenue-card"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-4 bg-rose-100 rounded-xl">
                      <Euro className="w-8 h-8 text-rose-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Paiements Concierges</h3>
                      <p className="text-sm text-slate-500">Voir les montants dus à chaque concierge</p>
                    </div>
                  </div>
                </Card>
                
                <Card 
                  className="bg-white border border-slate-100 shadow-sm rounded-xl p-6 cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate('/admin/analytics')}
                  data-testid="admin-analytics-card"
                >
                  <div className="flex items-center space-x-4">
                    <div className="p-4 bg-indigo-50 rounded-xl">
                      <BarChart3 className="w-8 h-8 text-indigo-600" strokeWidth={1.5} />
                    </div>
                    <div>
                      <h3 className="text-xl font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Analytics</h3>
                      <p className="text-sm text-slate-500">Tunnel de conversion et statistiques</p>
                    </div>
                  </div>
                </Card>
              </div>
              
              <Card className="bg-rose-50 border border-rose-200 rounded-xl p-6">
                <h3 className="text-lg font-medium text-rose-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  Bienvenue sur votre espace Admin
                </h3>
                <p className="text-sm text-rose-800">
                  Utilisez "Options Globales" pour créer et gérer les options que vos concierges peuvent proposer aux voyageurs. 
                  Consultez "Paiements Concierges" pour voir combien vous devez verser à chaque concierge.
                </p>
              </Card>
            </div>
          ) : (
            /* Dashboard Concierge - Mobile optimized */
            <div className="space-y-4 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-6">
              {/* Calendar - Full width on mobile, sidebar on desktop */}
              <div className="lg:col-span-3 order-2 lg:order-1">
                <CalendarView 
                  purchasedOptions={todos} 
                  onTaskUpdate={(taskId, newStatus) => {
                    setTodos(prevTodos => prevTodos.map(todo => 
                      todo.id === taskId 
                        ? { ...todo, task_completed: newStatus }
                        : todo
                    ).sort((a, b) => {
                      if (a.task_completed !== b.task_completed) {
                        return a.task_completed ? 1 : -1;
                      }
                      return new Date(a.date_debut) - new Date(b.date_debut);
                    }));
                  }}
                />
              </div>

              {/* To-Do List - Priority on mobile */}
              <div className="lg:col-span-6 order-1 lg:order-2">
                <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6">
                  <div className="flex items-center space-x-2 mb-4 sm:mb-6">
                    <CheckCircle2 className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
                    <h3 className="text-lg sm:text-xl font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Tâches à réaliser</h3>
                  </div>
                  
                  {todos.length === 0 ? (
                    <div className="text-center py-8 sm:py-12" data-testid="empty-todos-state">
                      <Package className="w-10 sm:w-12 h-10 sm:h-12 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
                      <p className="text-sm text-slate-500">Aucune option à mettre en place pour le moment</p>
                    </div>
                  ) : (
                    <div className="space-y-2 sm:space-y-3" data-testid="todos-list">
                      {todos.map((todo) => (
                        <div 
                          key={todo.id} 
                          className={`flex items-start space-x-3 p-3 sm:p-4 border rounded-lg transition-all ${
                            todo.task_completed 
                              ? 'bg-slate-50 border-slate-200 opacity-60' 
                              : todo.option_type === 'prestataire' && todo.statut_activite === 'pending_confirmation'
                                ? 'border-amber-200 bg-amber-50/30'
                                : 'border-slate-100 hover:bg-slate-50'
                          }`}
                        >
                          <Checkbox 
                            data-testid={`todo-checkbox-${todo.id}`} 
                            className="mt-1 flex-shrink-0" 
                            checked={todo.task_completed}
                            onCheckedChange={() => handleToggleTodo(todo.id, todo.task_completed)}
                            disabled={todo.option_type === 'prestataire' && todo.statut_activite === 'pending_confirmation'}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className={`font-medium text-sm sm:text-base ${todo.task_completed ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
                                {todo.titre_affiche || todo.option_name}
                              </p>
                              {/* Badge prestataire */}
                              {todo.option_type === 'prestataire' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                                  <UserCheck className="w-3 h-3 mr-1" />
                                  Prestataire
                                </span>
                              )}
                              {/* Badge statut activité */}
                              {todo.statut_activite === 'pending_confirmation' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">
                                  <Clock className="w-3 h-3 mr-1" />
                                  En attente
                                </span>
                              )}
                              {todo.statut_activite === 'confirmed' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Confirmée
                                </span>
                              )}
                            </div>
                            <p className={`text-xs sm:text-sm truncate ${todo.task_completed ? 'text-slate-400' : 'text-slate-600'}`}>
                              Logement: {todo.property_name}
                            </p>
                            <p className={`text-xs sm:text-sm ${todo.task_completed ? 'text-slate-400' : 'text-slate-500'}`}>
                              {todo.nom_voyageur} - {new Date(todo.date_debut).toLocaleDateString('fr-FR')}
                            </p>
                            
                            {/* Informations supplémentaires pour les activités prestataire */}
                            {todo.option_type === 'prestataire' && (
                              <div className="mt-2 p-3 rounded-lg bg-purple-50/50 border border-purple-100 space-y-2">
                                {/* Coordonnées voyageur */}
                                <div className="flex flex-wrap gap-3 text-xs">
                                  {todo.voyageur_telephone && (
                                    <span className="flex items-center gap-1 text-slate-700">
                                      <Phone className="w-3 h-3 text-purple-600" />
                                      {todo.voyageur_telephone}
                                    </span>
                                  )}
                                  {todo.voyageur_email && (
                                    <span className="flex items-center gap-1 text-slate-700">
                                      <Mail className="w-3 h-3 text-purple-600" />
                                      {todo.voyageur_email}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Participants et date */}
                                <div className="flex flex-wrap gap-3 text-xs">
                                  <span className="text-slate-700">
                                    <strong>Participants:</strong> {todo.nb_participants || 1}
                                  </span>
                                  <span className="text-slate-700">
                                    <strong>Tarif:</strong> {todo.type_tarif === 'groupe' ? 'Groupe' : 'Par personne'}
                                  </span>
                                  {todo.date_souhaitee && (
                                    <span className="text-slate-700">
                                      <strong>Date souhaitée:</strong> {new Date(todo.date_souhaitee).toLocaleDateString('fr-FR')}
                                    </span>
                                  )}
                                  {todo.date_confirmee && todo.date_confirmee !== todo.date_souhaitee && (
                                    <span className="text-green-700">
                                      <strong>Date confirmée:</strong> {new Date(todo.date_confirmee).toLocaleDateString('fr-FR')}
                                    </span>
                                  )}
                                </div>
                                
                                {/* Montants */}
                                <div className="flex flex-wrap gap-3 text-xs border-t border-purple-200 pt-2">
                                  <span className="text-slate-700">
                                    <strong>Prix payé:</strong> {todo.prix_paye?.toFixed(2)} €
                                  </span>
                                  <span className="text-amber-700">
                                    <strong>Dû prestataire:</strong> {todo.montant_prestataire?.toFixed(2)} €
                                  </span>
                                  <span className="text-green-700">
                                    <strong>Ma marge:</strong> {todo.montant_concierge?.toFixed(2)} €
                                  </span>
                                </div>
                                
                                {/* Prestataire */}
                                {todo.prestataire_nom && (
                                  <div className="text-xs bg-white p-2 rounded border border-purple-100">
                                    <p className="font-medium text-purple-800">Prestataire: {todo.prestataire_nom}</p>
                                    <div className="flex flex-wrap gap-2 mt-1 text-slate-600">
                                      {todo.prestataire_telephone && <span>{todo.prestataire_telephone}</span>}
                                      {todo.prestataire_email && <span>{todo.prestataire_email}</span>}
                                    </div>
                                  </div>
                                )}
                              </div>
                            )}
                            
                            {/* Affichage de la réponse du voyageur */}
                            {todo.question_answer && (
                              <div className={`mt-2 p-2 rounded-lg ${todo.task_completed ? 'bg-slate-100' : 'bg-blue-50 border border-blue-200'}`}>
                                <p className={`text-xs font-medium flex items-center gap-1 ${todo.task_completed ? 'text-slate-400' : 'text-blue-700'}`}>
                                  <MessageSquare className="w-3 h-3" />
                                  Réponse du voyageur :
                                </p>
                                <p className={`text-xs mt-1 ${todo.task_completed ? 'text-slate-400' : 'text-blue-900'}`}>
                                  "{todo.question_answer}"
                                </p>
                              </div>
                            )}
                            
                            {todo.task_completed && (
                              <p className="text-xs text-green-600 mt-1 flex items-center">
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Terminée
                              </p>
                            )}
                            
                            {/* Boutons d'action pour les activités prestataire en attente */}
                            {todo.option_type === 'prestataire' && todo.statut_activite === 'pending_confirmation' && (
                              <div className="flex flex-wrap gap-2 mt-3">
                                <Button
                                  variant="default"
                                  size="sm"
                                  data-testid={`confirm-activity-btn-${todo.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openConfirmDialog(todo);
                                  }}
                                  className="bg-green-600 hover:bg-green-700 text-white text-xs h-7 px-3"
                                >
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Confirmer la réservation
                                </Button>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  data-testid={`cancel-activity-btn-${todo.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openCancelDialog(todo);
                                  }}
                                  className="text-rose-600 border-rose-200 hover:bg-rose-50 text-xs h-7 px-3"
                                >
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Annuler & Rembourser
                                </Button>
                              </div>
                            )}
                            
                            {/* Bouton Annuler & Rembourser standard - seulement si non complétée et non prestataire en attente */}
                            {!todo.task_completed && !(todo.option_type === 'prestataire' && todo.statut_activite === 'pending_confirmation') && (
                              <Button
                                variant="ghost"
                                size="sm"
                                data-testid={`refund-btn-${todo.id}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRefund(todo.id, todo.option_name);
                                }}
                                disabled={refundingId === todo.id}
                                className="mt-2 text-rose-600 hover:text-rose-700 hover:bg-rose-50 text-xs h-7 px-2"
                              >
                                {refundingId === todo.id ? (
                                  <>
                                    <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                    Remboursement...
                                  </>
                                ) : (
                                  <>
                                    <XCircle className="w-3 h-3 mr-1" />
                                    Annuler & Rembourser
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Properties List - Last on mobile */}
              <div className="lg:col-span-3 order-3">
                <Card className="bg-white border border-slate-100 shadow-sm rounded-xl p-4 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Building2 className="w-5 h-5 text-rose-600" strokeWidth={1.5} />
                      <h3 className="text-lg sm:text-xl font-medium text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Logements</h3>
                    </div>
                    <Button
                      size="sm"
                      data-testid="add-property-quick-btn"
                      onClick={() => navigate('/properties')}
                      className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
                    >
                      +
                    </Button>
                  </div>

                  {properties.length === 0 ? (
                    <div className="text-center py-6 sm:py-8" data-testid="empty-properties-state">
                      <Building2 className="w-8 sm:w-10 h-8 sm:h-10 text-slate-300 mx-auto mb-2" strokeWidth={1.5} />
                      <p className="text-sm text-slate-500 mb-3">Aucun logement</p>
                      <Button
                        size="sm"
                        data-testid="add-first-property-btn"
                        onClick={() => navigate('/properties')}
                        className="bg-rose-600 hover:bg-rose-700 text-white rounded-lg"
                      >
                        Ajouter un logement
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2" data-testid="properties-quick-list">
                      {properties.slice(0, 5).map((property) => (
                        <div 
                          key={property.id} 
                          className="p-3 border border-slate-100 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                          onClick={() => navigate('/properties')}
                          data-testid={`property-item-${property.id}`}
                        >
                          <p className="font-medium text-slate-900 text-sm truncate">{property.nom}</p>
                          <p className="text-xs text-slate-500">{property.actif ? '✅ Actif' : '❌ Inactif'}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              </div>
            </div>
          )}
        </main>
      </div>
      
      {/* Modal de confirmation d'activité */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              Confirmer la réservation
            </DialogTitle>
            <DialogDescription>
              Confirmez que l'activité est disponible à la date demandée ou proposez une autre date.
            </DialogDescription>
          </DialogHeader>
          
          {confirmingActivity && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{confirmingActivity.titre_affiche || confirmingActivity.option_name}</p>
                <p className="text-sm text-slate-600">{confirmingActivity.nb_participants} participant(s)</p>
                <p className="text-sm text-slate-600">
                  Date souhaitée: {confirmingActivity.date_souhaitee ? new Date(confirmingActivity.date_souhaitee).toLocaleDateString('fr-FR') : 'Non spécifiée'}
                </p>
              </div>
              
              <div>
                <Label htmlFor="confirm-date">Date confirmée</Label>
                <Input
                  id="confirm-date"
                  type="date"
                  value={confirmDate}
                  onChange={(e) => setConfirmDate(e.target.value)}
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Laissez vide pour utiliser la date souhaitée par le voyageur
                </p>
              </div>
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  onClick={handleConfirmActivity}
                  disabled={confirmLoading}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {confirmLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Confirmation...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Confirmer
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Modal d'annulation et remboursement */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-rose-600" />
              Annuler et rembourser
            </DialogTitle>
            <DialogDescription>
              Le voyageur sera automatiquement remboursé et notifié par email.
            </DialogDescription>
          </DialogHeader>
          
          {cancelingActivity && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium">{cancelingActivity.titre_affiche || cancelingActivity.option_name}</p>
                <p className="text-sm text-slate-600">Montant à rembourser: {cancelingActivity.prix_paye?.toFixed(2)} €</p>
              </div>
              
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800 flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>
                    <strong>Attention:</strong> Vérifiez si vous avez déjà engagé des frais auprès du prestataire 
                    ({cancelingActivity.prestataire_nom}: {cancelingActivity.montant_prestataire?.toFixed(2)} €).
                  </span>
                </p>
              </div>
              
              <div>
                <Label htmlFor="cancel-reason">Motif d'annulation *</Label>
                <Select value={cancelReason} onValueChange={setCancelReason}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Sélectionner un motif..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_availability">Plus de disponibilité</SelectItem>
                    <SelectItem value="provider_cancelled">Annulation par le prestataire</SelectItem>
                    <SelectItem value="weather">Conditions météo</SelectItem>
                    <SelectItem value="other">Autre raison</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {cancelReason === 'other' && (
                <div>
                  <Label htmlFor="cancel-details">Précisez le motif *</Label>
                  <Textarea
                    id="cancel-details"
                    value={cancelDetails}
                    onChange={(e) => setCancelDetails(e.target.value)}
                    placeholder="Expliquez la raison de l'annulation..."
                    className="mt-1"
                    rows={3}
                  />
                </div>
              )}
              
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setCancelDialogOpen(false)}>
                  Annuler
                </Button>
                <Button 
                  variant="destructive"
                  onClick={handleCancelActivity}
                  disabled={cancelLoading || !cancelReason || (cancelReason === 'other' && !cancelDetails.trim())}
                >
                  {cancelLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4 mr-2" />
                      Annuler & Rembourser
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
